/**
 * Nutrition Facts Display Component
 * Displays nutrition information in a standardized format similar to FDA nutrition labels
 */

import React from 'react';
import { NutritionInformation } from '../services/qr.service';

interface NutritionFactsDisplayProps {
  nutritionInfo: NutritionInformation;
  className?: string;
}

const NutritionFactsDisplay: React.FC<NutritionFactsDisplayProps> = ({
  nutritionInfo,
  className = ''
}) => {
  const formatNumber = (value: number): string => {
    return value % 1 === 0 ? value.toString() : value.toFixed(1);
  };

  const calculateDailyValue = (nutrient: string, amount: number): number | null => {
    // Daily Value percentages based on 2000 calorie diet
    const dailyValues: { [key: string]: number } = {
      totalFat: 65,
      saturatedFat: 20,
      sodium: 2300,
      carbohydrates: 300,
      fiber: 25,
      protein: 50
    };

    const dv = dailyValues[nutrient];
    if (!dv) return null;
    
    return Math.round((amount / dv) * 100);
  };

  return (
    <div className={`bg-white border-2 border-black max-w-sm ${className}`}>
      {/* Header */}
      <div className="bg-black text-white p-2">
        <h3 className="text-lg font-bold text-center">Nutrition Facts</h3>
      </div>

      <div className="p-3 space-y-2">
        {/* Serving Information */}
        <div className="border-b-4 border-black pb-2">
          <div className="text-sm">
            <strong>Serving Size:</strong> {nutritionInfo.servingSize}
          </div>
          {nutritionInfo.servingsPerContainer && (
            <div className="text-sm">
              <strong>Servings Per Container:</strong> {nutritionInfo.servingsPerContainer}
            </div>
          )}
        </div>

        {/* Calories */}
        <div className="border-b-4 border-black pb-2">
          <div className="flex justify-between items-end">
            <span className="text-xl font-bold">Calories</span>
            <span className="text-2xl font-bold">{nutritionInfo.caloriesPerServing}</span>
          </div>
          {nutritionInfo.caloriesPer100g && (
            <div className="text-sm text-gray-600">
              ({nutritionInfo.caloriesPer100g} per 100g)
            </div>
          )}
        </div>

        {/* Daily Value Header */}
        <div className="text-right text-sm font-bold border-b border-black pb-1">
          % Daily Value*
        </div>

        {/* Macronutrients */}
        <div className="space-y-1">
          {/* Total Fat */}
          <div className="flex justify-between border-b border-gray-300 pb-1">
            <span className="font-bold">Total Fat {formatNumber(nutritionInfo.macronutrients.totalFat)}g</span>
            <span className="font-bold">
              {calculateDailyValue('totalFat', nutritionInfo.macronutrients.totalFat) || ''}
              {calculateDailyValue('totalFat', nutritionInfo.macronutrients.totalFat) ? '%' : ''}
            </span>
          </div>

          {/* Saturated Fat */}
          {nutritionInfo.macronutrients.saturatedFat !== undefined && (
            <div className="flex justify-between pl-4 text-sm">
              <span>Saturated Fat {formatNumber(nutritionInfo.macronutrients.saturatedFat)}g</span>
              <span>
                {calculateDailyValue('saturatedFat', nutritionInfo.macronutrients.saturatedFat) || ''}
                {calculateDailyValue('saturatedFat', nutritionInfo.macronutrients.saturatedFat) ? '%' : ''}
              </span>
            </div>
          )}

          {/* Trans Fat */}
          {nutritionInfo.macronutrients.transFat !== undefined && (
            <div className="pl-4 text-sm">
              <span>Trans Fat {formatNumber(nutritionInfo.macronutrients.transFat)}g</span>
            </div>
          )}

          {/* Sodium */}
          <div className="flex justify-between border-b border-gray-300 pb-1">
            <span className="font-bold">Sodium {formatNumber(nutritionInfo.macronutrients.sodium)}mg</span>
            <span className="font-bold">
              {calculateDailyValue('sodium', nutritionInfo.macronutrients.sodium) || ''}
              {calculateDailyValue('sodium', nutritionInfo.macronutrients.sodium) ? '%' : ''}
            </span>
          </div>

          {/* Total Carbohydrates */}
          <div className="flex justify-between border-b border-gray-300 pb-1">
            <span className="font-bold">Total Carbohydrate {formatNumber(nutritionInfo.macronutrients.carbohydrates)}g</span>
            <span className="font-bold">
              {calculateDailyValue('carbohydrates', nutritionInfo.macronutrients.carbohydrates) || ''}
              {calculateDailyValue('carbohydrates', nutritionInfo.macronutrients.carbohydrates) ? '%' : ''}
            </span>
          </div>

          {/* Dietary Fiber */}
          {nutritionInfo.macronutrients.fiber !== undefined && (
            <div className="flex justify-between pl-4 text-sm">
              <span>Dietary Fiber {formatNumber(nutritionInfo.macronutrients.fiber)}g</span>
              <span>
                {calculateDailyValue('fiber', nutritionInfo.macronutrients.fiber) || ''}
                {calculateDailyValue('fiber', nutritionInfo.macronutrients.fiber) ? '%' : ''}
              </span>
            </div>
          )}

          {/* Total Sugars */}
          {nutritionInfo.macronutrients.sugar !== undefined && (
            <div className="pl-4 text-sm">
              <span>Total Sugars {formatNumber(nutritionInfo.macronutrients.sugar)}g</span>
            </div>
          )}

          {/* Protein */}
          <div className="flex justify-between border-b-4 border-black pb-2">
            <span className="font-bold">Protein {formatNumber(nutritionInfo.macronutrients.protein)}g</span>
            <span className="font-bold">
              {calculateDailyValue('protein', nutritionInfo.macronutrients.protein) || ''}
              {calculateDailyValue('protein', nutritionInfo.macronutrients.protein) ? '%' : ''}
            </span>
          </div>
        </div>

        {/* Vitamins and Minerals */}
        {(nutritionInfo.vitamins?.length || nutritionInfo.minerals?.length) && (
          <div className="space-y-1 pt-2">
            {nutritionInfo.vitamins?.map((vitamin, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span>{vitamin.name} {formatNumber(vitamin.amount)}{vitamin.unit}</span>
                <span>{vitamin.dailyValuePercent ? `${vitamin.dailyValuePercent}%` : ''}</span>
              </div>
            ))}
            {nutritionInfo.minerals?.map((mineral, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span>{mineral.name} {formatNumber(mineral.amount)}{mineral.unit}</span>
                <span>{mineral.dailyValuePercent ? `${mineral.dailyValuePercent}%` : ''}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="border-t-4 border-black pt-2 text-xs">
          <p>* The % Daily Value tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.</p>
        </div>
      </div>
    </div>
  );
};

export default NutritionFactsDisplay;